'use client'

import { DatePicker, FilterChip, TravelerSelection, type DateRange, type ITravelerInfo } from '@pcln/horizon'
import { addDays } from 'date-fns/addDays'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { isValid } from 'date-fns/isValid'
import { parse } from 'date-fns/parse'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type SyntheticEvent,
} from 'react'

const FORM_DATE_FORMAT = 'yyyyMMdd'
const STAY_SEARCH_CALENDAR_MONTHS = 13
const HOTEL_MAX_ROOMS = 9
const HOTEL_MAX_TRAVELERS = 36
const DATE_POPOVER_HEADING = 'Hotels: Check-in - Check-out'

const FILTER_CHIP_TRIGGER = {
  opens: false as const,
  type: 'chip' as const,
  emphasis: 'regular' as const,
  showCheckIcon: false,
  showArrow: true,
}

// `value` is dropped because the overlay trigger's native button value is
// unrelated to FilterChip's own `value` prop.
type ChipTriggerProps = Omit<ComponentPropsWithoutRef<'button'>, 'value'> & {
  chipLabel: string
  iconLeft: 'date_range' | 'group'
  isOpen: boolean
}

const ChipTrigger = forwardRef<HTMLButtonElement, ChipTriggerProps>(function ChipTrigger(
  { chipLabel, iconLeft, isOpen, onClick, onMouseDown, ...triggerProps },
  ref,
) {
  const handleClick = useCallback(
    (event?: SyntheticEvent) => {
      if (event && onClick) {
        onClick(event as MouseEvent<HTMLButtonElement>)
      }
    },
    [onClick],
  )

  const handleMouseDown = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      onMouseDown?.(event as MouseEvent<HTMLButtonElement>)
    },
    [onMouseDown],
  )

  return (
    <FilterChip
      {...triggerProps}
      {...FILTER_CHIP_TRIGGER}
      {...(onMouseDown
        ? {
            onMouseDown: handleMouseDown,
          }
        : {})}
      ref={ref}
      label={chipLabel}
      iconLeft={iconLeft}
      selected={isOpen}
      onClick={handleClick}
    />
  )
})

function parseFormDateString(date: string | undefined, referenceDate = new Date()) {
  if (!date) return undefined

  const parsed = parse(date, FORM_DATE_FORMAT, referenceDate)
  return isValid(parsed) ? parsed : undefined
}

function getDateRangeFromFormDates(
  startDate: string | undefined,
  endDate: string | undefined,
  referenceDate = new Date(),
): DateRange | undefined {
  const from = parseFormDateString(startDate, referenceDate)
  if (!from) return undefined

  const to = parseFormDateString(endDate, referenceDate)
  return to ? { from, to } : { from }
}

export function formatDateChipLabel(startDate: string, endDate: string, referenceDate = new Date()) {
  const from = parseFormDateString(startDate, referenceDate)
  const to = parseFormDateString(endDate, referenceDate)

  if (!from) return 'Select dates'
  if (!to) return format(from, 'MMM d')

  return `${format(from, 'MMM d')}–${format(to, 'MMM d')}`
}

export type AiSearchFilterChipsProps = {
  startDate: string
  endDate: string
  travelers: ITravelerInfo
  onDatesChange: (startDate: string, endDate: string) => void
  onTravelersChange: (travelers: ITravelerInfo) => void
}

export default function AiSearchFilterChips({
  startDate,
  endDate,
  travelers,
  onDatesChange,
  onTravelersChange,
}: Readonly<AiSearchFilterChipsProps>) {
  const currentDate = useMemo(() => new Date(), [])
  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>()
  const [defaultMonth, setDefaultMonth] = useState(
    () => parseFormDateString(startDate, currentDate) ?? currentDate,
  )

  const selectedDates = useMemo(
    () => getDateRangeFromFormDates(startDate, endDate, currentDate),
    [currentDate, endDate, startDate],
  )

  const dateChipLabel = useMemo(
    () => formatDateChipLabel(startDate, endDate, currentDate),
    [currentDate, endDate, startDate],
  )

  const calendarEndMonth = addMonths(currentDate, STAY_SEARCH_CALENDAR_MONTHS - 1)

  // Completing a range makes DatePicker close in the same event it reports the change, so the
  // close handler runs before `selectedDates` reflects the new props. Track the latest range
  // separately or the auto-checkout default would overwrite a check-out the user just picked.
  const latestDatesRef = useRef(selectedDates)

  useEffect(() => {
    latestDatesRef.current = selectedDates
  }, [selectedDates])

  const handleCalendarChange = useCallback(
    (dates: DateRange, triggerDate: Date) => {
      latestDatesRef.current = dates?.from ? dates : undefined

      if (dates?.from) {
        onDatesChange(
          format(dates.from, FORM_DATE_FORMAT),
          dates.to ? format(dates.to, FORM_DATE_FORMAT) : '',
        )
        setDefaultMonth(dates.from)

        if (dates.to === undefined) {
          setMaxSelectableDate(addDays(dates.from, 28))
        } else {
          setMaxSelectableDate(undefined)
        }
      } else {
        onDatesChange('', '')
        setDefaultMonth(triggerDate)
        setMaxSelectableDate(undefined)
      }
    },
    [onDatesChange],
  )

  const handleDateOpenChange = useCallback(
    (open: boolean) => {
      if (open) return

      // If the user only picked a check-in date and closed the calendar without
      // picking a check-out date, default to a 1-night stay instead of leaving
      // the search with an incomplete date range.
      const dates = latestDatesRef.current
      if (dates?.from && !dates.to) {
        const autoCheckoutDate = addDays(dates.from, 1)
        onDatesChange(format(dates.from, FORM_DATE_FORMAT), format(autoCheckoutDate, FORM_DATE_FORMAT))
        setMaxSelectableDate(undefined)
      }
    },
    [onDatesChange],
  )

  return (
    <div className='flex flex-wrap items-center gap-2' data-testid='ai-search-filter-chips'>
      <div data-testid='ai-search-date-chip'>
        <DatePicker
          mode='range'
          label={DATE_POPOVER_HEADING}
          heading={DATE_POPOVER_HEADING}
          subHeading={dateChipLabel}
          selected={selectedDates}
          handleCalendarChange={handleCalendarChange}
          onOpenChange={handleDateOpenChange}
          numberOfMonths={2}
          min={1}
          defaultMonth={defaultMonth}
          startMonth={currentDate}
          endMonth={calendarEndMonth}
          disabled={{
            before: currentDate,
            after: maxSelectableDate ?? calendarEndMonth,
          }}
          renderTrigger={(triggerProps) => (
            <ChipTrigger {...triggerProps} chipLabel={dateChipLabel} iconLeft='date_range' />
          )}
        />
      </div>

      <div data-testid='ai-search-traveler-chip'>
        <TravelerSelection
          isHotel
          roomsEnabled
          ageSelectEnabled
          maxRooms={HOTEL_MAX_ROOMS}
          maxChildren={8}
          maxTravelers={HOTEL_MAX_TRAVELERS}
          initialValues={travelers}
          tooltipErrorMessageId='ai-search-traveler-selection-error'
          onChange={onTravelersChange}
          renderTrigger={({ value, ...triggerProps }) => (
            <ChipTrigger {...triggerProps} chipLabel={value} iconLeft='group' />
          )}
        />
      </div>
    </div>
  )
}
